import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LEAD_SOURCES, LEAD_STAGES, type LeadStage } from '@worksy360/shared';
import { api, ApiRequestError } from '../lib/api.js';
import { useLeads } from '../lib/hooks.js';
import { Badge, Button, Card, Drawer, EmptyState, Field, Input, Select, Skeleton, Textarea } from '../ui/index.js';
import { useToast } from '../ui/toast.js';
import { label, relTime, statusTone } from '../lib/format.js';

export function Leads() {
  const [stage, setStage] = useState<LeadStage>('new');
  const { data, isLoading, refetch } = useLeads(stage);
  const [sp, setSp] = useSearchParams();
  const [drawer, setDrawer] = useState(sp.get('new') === '1');
  const [busy, setBusy] = useState<string | null>(null);
  const toast = useToast();
  const navigate = useNavigate();
  const leads = data?.items ?? [];

  const convert = async (leadId: string) => {
    setBusy(leadId);
    try {
      const customer = await api.post<{ id: string }>(`/leads/${leadId}/convert`);
      toast.push('Lead converted to customer', 'success');
      navigate(`/customers/${customer.id}`);
    } catch (err) {
      toast.push(err instanceof ApiRequestError ? err.message : 'Convert failed', 'error');
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <div className="page-head">
        <div><div className="page-title">Leads</div><div className="page-sub">Your sales pipeline</div></div>
        <Button onClick={() => setDrawer(true)}>+ New lead</Button>
      </div>

      <div className="row gap-2 wrap" style={{ marginBottom: 'var(--sp-4)' }}>
        {LEAD_STAGES.map((s) => (
          <button key={s} className={`btn ${s === stage ? 'btn-primary' : 'btn-secondary'} btn-sm`} onClick={() => setStage(s)}>{label(s)}</button>
        ))}
      </div>

      <Card>
        {isLoading ? (
          <div className="card-pad stack gap-3">{[0, 1, 2].map((i) => <Skeleton key={i} h={22} />)}</div>
        ) : leads.length === 0 ? (
          <EmptyState icon="✦" title={`No ${label(stage).toLowerCase()} leads`} action={<Button onClick={() => setDrawer(true)}>Capture a lead</Button>}>
            Website and phone leads land here. Convert a lead to a customer with one click — no re-typing.
          </EmptyState>
        ) : (
          <div style={{ padding: '0 var(--sp-5)' }}>
            {leads.map((l) => (
              <div key={l.id} className="queue-item">
                <div className="stack">
                  <div className="row gap-2"><strong>{l.name}</strong><Badge tone={statusTone(l.stage)}>{label(l.stage)}</Badge><Badge tone="neutral">{label(l.source)}</Badge></div>
                  <span className="subtle" style={{ fontSize: 'var(--text-xs)' }}>{l.phone ?? l.email ?? ''} · {relTime(l.createdAt)}{l.serviceNeeded ? ` · ${l.serviceNeeded}` : ''}</span>
                </div>
                {l.stage !== 'won' && l.stage !== 'lost' && <Button size="sm" loading={busy === l.id} onClick={() => convert(l.id)}>Convert →</Button>}
                {l.convertedCustomerId && <Button size="sm" variant="ghost" onClick={() => navigate(`/customers/${l.convertedCustomerId}`)}>View customer</Button>}
              </div>
            ))}
          </div>
        )}
      </Card>

      {drawer && <NewLeadDrawer onClose={() => { setDrawer(false); if (sp.get('new')) { sp.delete('new'); setSp(sp, { replace: true }); } }} onCreated={() => { setDrawer(false); refetch(); toast.push('Lead captured', 'success'); }} />}
    </>
  );
}

function NewLeadDrawer({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const toast = useToast();
  const [f, setF] = useState({ name: '', phone: '', email: '', source: 'phone', serviceNeeded: '' });
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setErrs({}); setSaving(true);
    try {
      await api.post('/leads', {
        name: f.name, phone: f.phone || undefined, email: f.email || undefined,
        source: f.source, serviceNeeded: f.serviceNeeded || undefined,
      });
      onCreated();
    } catch (err) {
      if (err instanceof ApiRequestError) { setErrs(err.fields ?? {}); toast.push(err.message, 'error'); }
    } finally { setSaving(false); }
  };
  return (
    <Drawer title="New lead" onClose={onClose} footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={saving} onClick={submit}>Save lead</Button></>}>
      <div className="stack gap-4">
        <Field label="Name" error={errs.name}><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus /></Field>
        <Field label="Phone" error={errs.phone}><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} inputMode="tel" placeholder="(555) 123-4567" /></Field>
        <Field label="Email" error={errs.email}><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} type="email" /></Field>
        <Field label="Source"><Select value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })}>{LEAD_SOURCES.map((s) => <option key={s} value={s}>{label(s)}</option>)}</Select></Field>
        <Field label="What do they need?"><Textarea value={f.serviceNeeded} onChange={(e) => setF({ ...f, serviceNeeded: e.target.value })} placeholder="e.g. No cooling upstairs, wants a quote this week" /></Field>
      </div>
    </Drawer>
  );
}
